/**
 * UUID - short UUID generation using nanoid.
 */

import { customAlphabet } from 'nanoid';

const shortUUIDAlphabet = '1234567890abcdef';
const shortUUIDGenerator = customAlphabet(shortUUIDAlphabet, 16);
const shortUUIDDefaultLength = 16;

let deterministicShortUUIDState: number | undefined;

/** Lightweight FNV-1a hash for converting strings into a deterministic 32-bit seed. */
function hashStringToUint32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) || 0x9e3779b9;
}

/** Xorshift32 step; deterministic and fast for ID generation. */
function xorshift32(state: number): number {
  let next = state | 0;
  next ^= next << 13;
  next ^= next >>> 17;
  next ^= next << 5;
  return (next >>> 0) || 0x9e3779b9;
}

function generateDeterministicShortUUID(initialState: number, length: number): string {
  let state = initialState || 0x9e3779b9;
  let result = '';
  for (let i = 0; i < length; i++) {
    state = xorshift32(state);
    result += shortUUIDAlphabet[state % shortUUIDAlphabet.length];
  }
  return result;
}

/**
 * Enables deterministic short UUID generation based on a provided seed.
 * Pass undefined to restore default random generation.
 */
export function configureShortUUIDSeed(seed?: string): void {
  if (seed == null) {
    deterministicShortUUIDState = undefined;
    return;
  }
  deterministicShortUUIDState = hashStringToUint32(seed);
}

/** Generates a deterministic short UUID from a stable input key. */
export function shortUUIDFrom(input: string, length: number = shortUUIDDefaultLength): string {
  const state = hashStringToUint32(input);
  return generateDeterministicShortUUID(state, length);
}

export function shortUUID(length?: number): string {
  if (deterministicShortUUIDState !== undefined) {
    deterministicShortUUIDState = xorshift32(deterministicShortUUIDState);
    return generateDeterministicShortUUID(deterministicShortUUIDState, length ?? shortUUIDDefaultLength);
  }
  return shortUUIDGenerator(length);
}

/**
 * Recursively replaces all `uuid` values in a plain object with newly generated short UUIDs,
 * or removes `uuid` keys when `deleteKey` is set.
 * This is the canonical implementation — editor and game code should use this (re-exported from `@gnsx/genesys.js` via `utils/index.js`, not a duplicate).
 */
export function replace_uuids(target: object, options: { deleteKey: boolean } = { deleteKey: false }): void {
  if (Array.isArray(target)) {
    target.forEach(item => replace_uuids(item, options));
    return;
  }
  if (typeof target === 'object' && target !== null) {
    Object.keys(target).forEach(key => {
      if (key === 'uuid') {
        if (options.deleteKey) {
          delete (target as Record<string, any>)[key];
        } else {
          (target as Record<string, any>)[key] = shortUUID();
        }
      } else {
        replace_uuids((target as Record<string, any>)[key], options);
      }
    });
  }
}
